import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * `<nb-skeleton>` — a loading placeholder with a quiet shimmer (design.md §5.5).
 *
 * Sizes itself from the `width` / `height` inputs (any CSS length) and rounds
 * with `radius` (or a full circle when `circle` is set). The shimmer animates
 * `--sunken` → `--sunken-2`; under `prefers-reduced-motion` it falls back to a
 * static `--sunken` fill. Purely decorative — it carries `aria-hidden` so AT
 * announces the surrounding `role="status"` region (e.g. `nb-state-view`)
 * rather than the placeholder boxes.
 *
 * @example
 * <nb-skeleton width="180px" height="20px" />
 * <nb-skeleton width="46px" height="46px" circle />
 */
@Component({
  selector: 'nb-skeleton',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sk',
    'aria-hidden': 'true',
    '[style.width]': 'width()',
    '[style.height]': 'height()',
    '[style.border-radius]': "circle() ? '50%' : radius()",
  },
  template: '',
  styleUrl: './skeleton.css',
})
export class NbSkeleton {
  /** Box width — any CSS length. Defaults to filling the container. */
  readonly width = input<string>('100%');
  /** Box height — any CSS length. Defaults to a single text line. */
  readonly height = input<string>('16px');
  /** Corner radius; ignored when `circle` is set. */
  readonly radius = input<string>('var(--r-sm)');
  /** Render a full circle (overrides `radius`) — for avatars/dots. */
  readonly circle = input(false);
}
